// 简易 sessionStorage 缓存 —— stale-while-revalidate 模式，给 admin SPA 顶级
// 数据（whoami / 菜单 overlay）消除"白屏 → 加载中 → 数据"两段闪烁：
//
//   useStaleCache(key, fetcher)
//     ↓
//   首次渲染就拿到上一次会话存的值（即使是 1s 前的也比空白强），
//   同时后台静默 fetch；新值回来再 setState 一次，但 UI 不会跳。
//
// sessionStorage 而非 localStorage：tab 关闭即丢，避免上次留下的脏数据骗
// 当前 tab；同时不会跨用户泄露（sessionStorage 不跨 tab 共享）。
//
// 简单 JSON 序列化 —— 这里只放小型结构（邮箱字符串 / 几十条菜单），不需 LRU。

import { useEffect, useState } from 'react'

function readCache<T>(key: string): T | undefined {
  if (typeof sessionStorage === 'undefined') return undefined
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return undefined
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

function writeCache<T>(key: string, value: T): void {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.setItem(key, JSON.stringify(value)) } catch { /* quota / serialize */ }
}

export function clearCache(key: string): void {
  if (typeof sessionStorage === 'undefined') return
  try { sessionStorage.removeItem(key) } catch { /* ignore */ }
}

/**
 * Stale-while-revalidate hook：
 *   - 首次渲染就用 cached（如果有）作为初值；UI 立刻成型。
 *   - 同时跑 fetcher；成功 → 写入 cache + setState；失败 → 调 onError（如 401 跳登录）。
 *   - cached 命中时不会进入 loading 状态；首次 cold 才会 undefined。
 *
 * 调用方应把 key 设成稳定字符串。fetcher 引用变了不会重跑（依赖只有 key）。
 */
export function useStaleCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: { skip?: boolean; onError?: (err: unknown) => void } = {},
): { data: T | undefined; loading: boolean } {
  const [data, setData] = useState<T | undefined>(() => readCache<T>(key))
  const [loading, setLoading] = useState(data === undefined)

  useEffect(() => {
    if (options.skip) return
    let cancelled = false
    fetcher()
      .then((fresh) => {
        if (cancelled) return
        writeCache(key, fresh)
        setData(fresh)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoading(false)
        options.onError?.(err)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, options.skip])

  return { data, loading }
}
